import React, { useEffect, useState } from "react";
import { istSichtbar } from "./bedingung";
import api from "./axiosSetup";
import Select from "react-select";
import CustomNavbar from "./CustomNavbar";
import { useNavigate, useLocation } from "react-router-dom";
import cfg from "../config";

const SRVSparePartEdit = () => {
  const [formData, setFormData] = useState({});
  const [formSchema, setFormSchema] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [haesList, setHaesList] = useState([]);
  const [selectedHaes, setSelectedHaes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});

  const navigate = useNavigate();
  const location = useLocation();

  // Optionaler Zugriff: Wer die Adresse direkt aufruft — per Lesezeichen oder
  // Link — kommt ohne Navigationszustand an. Ohne das Fragezeichen brach die
  // Ansicht dann mit "Etwas ist schiefgelaufen" ab, statt das Formular für
  // eine neue Bestellung zu zeigen.
  const order = location.state?.orderId || null;
  const orderId = typeof order === "number" ? order : order?.Bestellnummer || null;

  // -------------------------------
  // 🔹 Formularschema + ggf. Bestelldaten laden
  // -------------------------------
  useEffect(() => {
    const loadFormAndData = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const schemaEndpoint = orderId
          ? "/api/form/srvSparePartEdit"
          : "/api/form/srvSparePartCreate";

        const schemaRes = await api.get(schemaEndpoint);
        setFormSchema(schemaRes.data);
        setFormFields(schemaRes.data.fields || []);

        if (orderId) {
          const orderRes = await api.get(`/api/sparepart/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          const orderData = orderRes.data;
          const initial = {};

          // Standardfelder aus Schema
          (schemaRes.data.fields || []).forEach((f) => {
            let val = orderData[f.name];
            if (f.type === "checkbox") val = !!val;
            if (f.type === "multiselect" && Array.isArray(val))
              val = val.map((v) => (typeof v === "object" ? v.value : v));
            initial[f.name] = val ?? (f.type === "checkbox" ? false : f.type === "multiselect" ? [] : "");
          });

          // Bekannte Felder
          initial.haesId = orderData.haesId || orderData.Haesnummer;
          initial.haesTyp = orderData.haesTyp || orderData.Häs;
          initial.haesNummer = orderData.haesNummer || "";
          initial.commentary = orderData.commentary || orderData.Kommentar;
          initial.status = orderData.status || "offen";

          // Dynamische Items
          const section = schemaRes.data.fields.find(
            (f) => f.type === "section" && f.dynamicItems
          );
          if (section) {
            const itemUpdates = {};
            section.dynamicItems.forEach((item) => {
              if (!item.allowedFor.includes(initial.haesTyp)) return;
              const subFields = item.fields || [];

              if (subFields.length === 0) {
                // field-less item (Schellengurt, Mondstab etc.) — marker in orderData
                if (orderData[item.name] !== undefined) {
                  itemUpdates[item.name] = true;
                }
                return;
              }

              let itemHasData = false;
              subFields.forEach((f) => {
                const v = orderData[f.name];
                if (v !== undefined) { initial[f.name] = v; itemHasData = true; }
              });
              if (itemHasData) itemUpdates[item.name] = true;
            });
            setSelectedItems(itemUpdates);
          }

          setFormData(initial);
          setSelectedHaes({ id: initial.haesId, haesTyp: initial.haesTyp });
        }

        setLoading(false);
      } catch (err) {
        console.error("[SRVSparePartEdit] Fehler beim Laden:", err);
        setLoading(false);
      }
    };

    loadFormAndData();
  }, [orderId, order]);

  // -------------------------------
  // 🔹 Häs-Liste laden
  // -------------------------------
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    api
      .get("/api/haes/list-by-member", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setHaesList(res.data))
      .catch((err) => console.error(err));
  }, []);

  // -------------------------------
  // 🔹 Handler
  // -------------------------------
  const handleHaesSelect = (e) => {
    const haesId = e.target.value;
    const selected = haesList.find((h) => String(h.id) === haesId);
    setSelectedHaes(selected);
  
    // 🔹 Kommentar und Status beibehalten
    const preserveFields = ["commentary", "status", "state"];
    const newFormData = {
      haesId,
      haesTyp: selected?.haesTyp || "",
    };
  
    formFields.forEach((f) => {
      if (preserveFields.includes(f.name)) {
        // beibehalten
        newFormData[f.name] = formData[f.name];
      } else {
        switch (f.type) {
          case "checkbox":
            newFormData[f.name] = false;
            break;
          case "multiselect":
            newFormData[f.name] = [];
            break;
          default:
            newFormData[f.name] = "";
        }
      }
    });
  
    setFormData(newFormData);
    setSelectedItems({});
  };
  
  
  

  const handleChange = (e, name) => setFormData({ ...formData, [name]: e.target.value });
  const handleCheckbox = (e, name) => setFormData({ ...formData, [name]: e.target.checked });
  const handleSelect = (e, name) => setFormData({ ...formData, [name]: e.target.value });
  const handleMultiSelect = (opts, name) =>
    setFormData({ ...formData, [name]: opts ? opts.map((o) => o.value) : [] });
  const handleItemToggle = (itemName) =>
    setSelectedItems((prev) => ({ ...prev, [itemName]: !prev[itemName] }));

  // -------------------------------
  // 🔹 Dynamische Items rendern
  // -------------------------------
  const renderDynamicItems = () => {
    if (!selectedHaes || !formSchema?.fields) return null;

    const section = formSchema.fields.find((f) => f.type === "section" && f.dynamicItems);
    if (!section) return null;

    const filtered = section.dynamicItems.filter((item) =>
      item.allowedFor?.includes(selectedHaes.haesTyp)
    );

    if (filtered.length === 0) return null;

    return (
      <>
        <h5 className="mt-4 mb-2">{section.label}</h5>
        {filtered.map((item, i) => (
          <div key={i} className="border rounded p-3 mb-3 bg-light text-dark">
            <div className="form-check mb-2">
              <input
                type="checkbox"
                id={item.name}
                className="form-check-input"
                checked={!!selectedItems[item.name]}
                onChange={() => handleItemToggle(item.name)}
              />
              <label className="form-check-label ms-1" htmlFor={item.name}>
                {item.name}
              </label>
            </div>
            {selectedItems[item.name] &&
              item.fields?.map((f, idx) => {
                switch (f.type) {
                  case "select":
                    return (
                      <div className="mb-2" key={idx}>
                        <label className="form-label">{f.label}{f.required && <span className="text-danger ms-1">*</span>}</label>
                        <select
                          className="form-select"
                          value={formData[f.name] || ""}
                          onChange={(e) => handleSelect(e, f.name)}
                          required={f.required}
                        >
                          <option value="">-- bitte auswählen --</option>
                          {f.options?.map((o, oIdx) => (
                            <option key={oIdx} value={o.value}>
                              {o["frontend-text"]}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  case "checkbox":
                    return (
                      <div className="form-check mb-2" key={idx}>
                        <input
                          type="checkbox"
                          id={f.name}
                          className="form-check-input"
                          checked={!!formData[f.name]}
                          onChange={(e) => handleCheckbox(e, f.name)}
                        />
                        <label className="form-check-label ms-1" htmlFor={f.name}>
                          {f.label}
                        </label>
                      </div>
                    );
                  case "number":
                    return (
                      <div className="mb-2" key={idx}>
                        <label className="form-label" htmlFor={f.name}>{f.label}{f.required && <span className="text-danger ms-1">*</span>}</label>
                        <input
                          type="number"
                          className="form-control"
                          id={f.name}
                          value={formData[f.name] || ""}
                          onChange={(e) => handleChange(e, f.name)}
                          required={f.required}
                        />
                      </div>
                    );
                  default:
                    return null;
                }
              })}
          </div>
        ))}
      </>
    );
  };

  // -------------------------------
  // 🔹 Standardfelder rendern
  // -------------------------------
  const renderFields = () =>
    formFields.map((f, i) => {
      if (!istSichtbar(f, formData)) return null;
      const disabled = !!f.disabled;
      switch (f.type) {
        case "select":
          return (
            <div className="mb-3" key={i}>
              <label className="form-label">{f.label}{f.required && <span className="text-danger ms-1">*</span>}</label>
              <select
                className="form-select"
                value={formData[f.name] || ""}
                onChange={(e) => handleSelect(e, f.name)}
                required={f.required}
                disabled={disabled}
              >
                <option value="">-- bitte auswählen --</option>
                {f.options?.map((o, idx) => (
                  <option key={idx} value={o.value}>{o["frontend-text"]}</option>
                ))}
              </select>
            </div>
          );
        case "multiselect":
          return (
            <div className="mb-3" key={i}>
              <label className="form-label">{f.label}{f.required && <span className="text-danger ms-1">*</span>}</label>
              <Select
                isMulti
                options={f.options?.map((o) => ({ value: o.value, label: o["frontend-text"] })) || []}
                value={(formData[f.name] || []).map((v) => ({
                  value: v,
                  label: f.options?.find((o) => o.value === v)?.["frontend-text"] || v,
                }))}
                onChange={(sel) => handleMultiSelect(sel, f.name)}
                isDisabled={disabled}
              />
            </div>
          );
        case "checkbox":
          return (
            <div className="form-check mb-3" key={i}>
              <input
                type="checkbox"
                className="form-check-input"
                id={f.name}
                checked={!!formData[f.name]}
                onChange={(e) => handleCheckbox(e, f.name)}
                disabled={disabled}
              />
              <label className="form-check-label" htmlFor={f.name}>{f.label}{f.required && <span className="text-danger ms-1">*</span>}</label>
            </div>
          );
        case "number":
          return (
            <div className="mb-3" key={i}>
              <label className="form-label" htmlFor={f.name}>{f.label}{f.required && <span className="text-danger ms-1">*</span>}</label>
              <input
                type="number"
                className="form-control"
                id={f.name}
                value={formData[f.name] || ""}
                onChange={(e) => handleChange(e, f.name)}
                disabled={disabled}
                required={f.required}
              />
            </div>
          );
        case "textarea":
          return (
            <div className="mb-3" key={i}>
              <label className="form-label">{f.label}{f.required && <span className="text-danger ms-1">*</span>}</label>
              <textarea
                className="form-control"
                rows={f.rows || 4}
                value={formData[f.name] || ""}
                onChange={(e) => handleChange(e, f.name)}
                required={f.required}
                disabled={disabled}
              />
            </div>
          );
        case "section":
          return <></>;
        default:
          return (
            <div className="mb-3" key={i}>
              <label className="form-label">{f.label}{f.required && <span className="text-danger ms-1">*</span>}</label>
              <input
                type={f.type}
                className="form-control"
                value={formData[f.name] || ""}
                onChange={(e) => handleChange(e, f.name)}
                required={f.required}
                disabled={disabled}
              />
            </div>
          );
      }
    });

  // -------------------------------
  // 🔹 Absenden
  // -------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    const invalid = formFields.filter((f) => f.required && !formData[f.name]).map((f) => f.label);

    // Die Pflichtangaben der gewaehlten Teile zaehlen mit - aber nur die.
    // Ein Feld, dessen Teil gar nicht angekreuzt ist, steht nicht im
    // Formular und kann auch nicht verlangt werden.
    const abschnitt = formSchema?.fields?.find((f) => f.type === "section" && f.dynamicItems);
    (abschnitt?.dynamicItems || [])
      .filter((item) => selectedItems[item.name])
      .forEach((item) => {
        (item.fields || []).forEach((f) => {
          if (f.required && !formData[f.name]) invalid.push(`${item.name}: ${f.label}`);
        });
      });

    if (invalid.length) {
      setErrors(invalid);
      return;
    }

    const dynamicFields = Object.keys(selectedItems)
      .filter((key) => selectedItems[key])
      .flatMap((itemName) => {
        const item = formSchema.fields
          .find((f) => f.type === "section" && f.dynamicItems)
          ?.dynamicItems.find((d) => d.name === itemName);
        if (!item) return [];
        if (!item.fields || item.fields.length === 0) {
          // field-less item: als Checkbox-Marker speichern
          return [{ name: item.name, type: "checkbox", value: true, displayName: item.name }];
        }
        return item.fields.map((f) => ({ name: f.name, type: f.type, value: formData[f.name], displayName: item.name }));
      });

    // Ohne Position ist die Bestellung leer. Nur beim Anlegen hart blockieren:
    // beim Bearbeiten muss der Zunftrat Altbestellungen ohne Positionen noch
    // auf einen Status setzen koennen. Fuer Mitglieder greift dort der Server.
    if (!orderId && !dynamicFields.length) {
      setErrors(["Bitte mindestens ein Ersatzteil auswählen."]);
      return;
    }

    const payload = {
      haes_id: formData.haesId,
      haes_typ: formData.haesTyp,
      commentary: formData.commentary,
      fields: dynamicFields,
      state: formData.state
    };

    if (orderId) payload.orderId = orderId;

    try {
      const token = localStorage.getItem("authToken");
      const endpoint = orderId ? "/api/update-sparepart" : "/api/create-sparepart";

      await api.post(endpoint, payload, { headers: { Authorization: `Bearer ${token}` } });
        // Ohne Herkunft ins Profil: dort stehen die eigenen Bestellungen.
        const fromRoute = location.state?.from || "/meine-daten";
        navigate(fromRoute, {
          state: {
            message: orderId
              ? "Die Ersatzteil-Bestellung wurde erfolgreich bearbeitet!"
              : "Die Ersatzteil-Bestellung wurde erfolgreich angelegt!"
          },
        });
      window.scrollTo(0, 0);
    } catch (err) {
      console.error(err);
      // Der Server begruendet die Ablehnung (z. B. fehlende Position). Diese
      // Begruendung ist hilfreicher als ein allgemeines "Fehler beim Absenden".
      setErrors([err.response?.data?.message || "Fehler beim Absenden. Bitte erneut versuchen."]);
    }
  };

  if (loading) return <div className="p-4">⏳ Lade Formular…</div>;

  return (
    <div className="container mt-4">
      <CustomNavbar />
      <h2>{formSchema?.title || (orderId ? "Ersatzteil-Bestellung bearbeiten" : "Neue Ersatzteil-Bestellung anlegen")}</h2>

      {errors.length > 0 && (
        <div className="alert alert-danger mt-3">Fehler: {errors.join(", ")}</div>
      )}

      <form onSubmit={handleSubmit} className="mt-3">
        {orderId ? (
          <div className="mb-3">
            <label className="form-label">{cfg.costumeTerm}</label>
            <input
              type="text"
              className="form-control"
              value={formData.haesNummer ? `${formData.haesNummer} (${formData.haesTyp})` : (formData.haesTyp || "—")}
              disabled
            />
          </div>
        ) : (
          <>
            <div className="mb-3">
              <label className="form-label">Mein {cfg.costumeTerm}</label>
              <select
                className="form-select"
                value={formData.haesId || ""}
                onChange={handleHaesSelect}
                required
              >
                <option value="">-- bitte auswählen --</option>
                {haesList.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.number} ({h.haesTyp})
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">{cfg.costumeTerm}-Typ</label>
              <input type="text" className="form-control" value={formData.haesTyp || ""} disabled />
            </div>
          </>
        )}

        {renderDynamicItems()}
        {renderFields()}

        <button type="submit" className="btn btn-primary mt-3">
          {orderId ? "Speichern" : "Anlegen"}
        </button>
      </form>
    </div>
  );
};

export default SRVSparePartEdit;
